import { FormHandles } from '@unform/core';
import React, { useCallback, useMemo } from 'react';

import { Switch } from '../Switch';
import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  EMPLOYEE_PRESET,
  GYM_MANAGER_PRESET,
  SENSITIVE_CODES,
  groupCatalogByCategory,
  inferRoleLabel,
  IPermission,
} from '../../utils/permissionPresets';

import {
  CategoryCount,
  CategoryHeader,
  CategoryLabel,
  CategorySection,
  Label,
  PermissionDescription,
  PermissionRow,
  PermissionsList,
  PresetCard,
  PresetCardDescription,
  PresetCardTitle,
  PresetsRow,
  SensitiveTag,
} from './styles';

interface IGroupedPermissionsEditorProps {
  catalog: IPermission[];
  formRef: React.RefObject<FormHandles>;
  // Estado salvo do vínculo -- usado só pros contadores/destaque de
  // preset (não é atualizado ao vivo enquanto o admin mexe nos
  // switches antes de salvar, ver plano).
  currentPermissions: string[];
  // Opcional -- item de escopo `platform` (hoje só `manage_platform_gyms`)
  // pra oferecer também um preset/linha de "área administrativa". Só faz
  // sentido em telas de admin de plataforma (associateGymUser/
  // AdminUserDetail) -- nunca em ChangeDataUser.
  platformPermission?: IPermission;
}

const GroupedPermissionsEditor: React.FC<IGroupedPermissionsEditorProps> = ({
  catalog,
  formRef,
  currentPermissions,
  platformPermission,
}) => {
  const groups = useMemo(() => groupCatalogByCategory(catalog), [catalog]);

  const currentSet = useMemo(
    () => new Set(currentPermissions),
    [currentPermissions],
  );

  const role = useMemo(
    () => inferRoleLabel(currentPermissions),
    [currentPermissions],
  );

  const platformGranted = !!(
    platformPermission && currentSet.has(platformPermission.code)
  );

  const applyPreset = useCallback(
    (codes: string[], grantPlatform: boolean) => {
      catalog.forEach(permission => {
        if (permission.code === 'student') {
          return;
        }

        formRef.current?.setFieldValue(
          permission.code,
          codes.includes(permission.code),
        );
      });

      if (platformPermission) {
        formRef.current?.setFieldValue(platformPermission.code, grantPlatform);
      }
    },
    [catalog, formRef, platformPermission],
  );

  return (
    <>
      <Label>Permissões:</Label>

      <PresetsRow>
        <PresetCard
          type="button"
          active={role === 'ALUNO'}
          onClick={() => applyPreset([], false)}
        >
          <PresetCardTitle>Nenhuma (aluno)</PresetCardTitle>
          <PresetCardDescription>
            Só acessa o próprio treino
          </PresetCardDescription>
        </PresetCard>

        <PresetCard
          type="button"
          active={role === 'FUNCIONÁRIO'}
          onClick={() => applyPreset(EMPLOYEE_PRESET, false)}
        >
          <PresetCardTitle>Funcionário</PresetCardTitle>
          <PresetCardDescription>
            Atende alunos, não administra
          </PresetCardDescription>
        </PresetCard>

        <PresetCard
          type="button"
          active={role === 'GESTOR' && !platformGranted}
          onClick={() => applyPreset(GYM_MANAGER_PRESET, false)}
        >
          <PresetCardTitle>Gestor da academia</PresetCardTitle>
          <PresetCardDescription>Acesso total à unidade</PresetCardDescription>
        </PresetCard>

        {platformPermission && (
          <PresetCard
            type="button"
            active={role === 'GESTOR' && platformGranted}
            onClick={() => applyPreset(GYM_MANAGER_PRESET, true)}
          >
            <PresetCardTitle>Admin de plataforma</PresetCardTitle>
            <PresetCardDescription>
              Gestão de todas as academias
            </PresetCardDescription>
          </PresetCard>
        )}
      </PresetsRow>

      <PermissionsList>
        {groups.map(group => (
          <CategorySection key={group.code}>
            <CategoryHeader>
              <Icon name={group.icon} size={16} color={colors.primary} />
              <CategoryLabel>{group.label}</CategoryLabel>
              <CategoryCount>
                {group.permissions.filter(p => currentSet.has(p.code)).length}/
                {group.permissions.length}
              </CategoryCount>
            </CategoryHeader>

            {group.permissions.map(permission => (
              <PermissionRow key={permission.id}>
                <Switch name={permission.code} />
                <PermissionDescription>
                  {permission.description}
                </PermissionDescription>
                {SENSITIVE_CODES.has(permission.code) && (
                  <SensitiveTag>Sensível</SensitiveTag>
                )}
              </PermissionRow>
            ))}
          </CategorySection>
        ))}

        {platformPermission && (
          <CategorySection>
            <CategoryHeader>
              <Icon name="shield_person" size={16} color={colors.primary} />
              <CategoryLabel>Plataforma</CategoryLabel>
              <CategoryCount>{platformGranted ? 1 : 0}/1</CategoryCount>
            </CategoryHeader>

            <PermissionRow>
              <Switch name={platformPermission.code} />
              <PermissionDescription>
                {platformPermission.description} (plataforma)
              </PermissionDescription>
              <SensitiveTag>Sensível</SensitiveTag>
            </PermissionRow>
          </CategorySection>
        )}
      </PermissionsList>
    </>
  );
};

export { GroupedPermissionsEditor };
